import { Component, Inject, Input } from '@angular/core';
import { includes } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { IFlag } from 'Src/ng2/shared/typings/interfaces/flags.interface';
import { IMilestone } from 'Src/ng2/shared/typings/interfaces/milestones.interface';
import { IStudentSupport } from 'Src/ng2/shared/typings/interfaces/student-support.interface';
import { IStudent } from 'Src/ng2/shared/typings/interfaces/student.interface';
import { IStudentPath } from 'Src/ng2/shared/typings/interfaces/studentPaths.interface';
import { ISchool } from '../../shared/typings/interfaces/school.interface';
import { SubjectAreas } from './../../../ng2/shared/constants/subject-areas.constant';
import { CLASS_OF_MAPPING } from './../../shared/constants/class-of-mapping.constant';
import { GRADE_MAPPING } from './../../shared/constants/grade-mapping.constant';
import { LIST_TYPE } from './../../shared/constants/list-view/list-view.constant';
import { CourseCodeGrouping } from './../../shared/services/mad-lib-services/grouping-functions/course-code/course-code';
import { MadLibGroupings } from './../../shared/services/mad-lib-services/mad-lib-groupings';
import { MadLibHelpers } from './../../shared/services/mad-lib-services/mad-lib-helpers';
import {
  ICollectionToMerge,
  StudentCollectionsMergeService,
} from './../../shared/services/student-collections-merge/student-collections-merge.service';
import { VALID_FLAG_TYPES } from './../graph-detail/graph-detail.constant';
import { STUDENT_LEVEL_LIST_COLUMNS } from './graph-list.constant';

@Component({
  selector: 'graph-list',
  template: require('./graph-list.component.html'),
  styles: [require('./graph-list.scss')],
})
export class GraphListComponent {
  graphName: string;
  title: string;
  columns: any[];
  studentGrouping$;
  studentsWithFlags$;
  midLevelGrouping;
  formattedGraphName: string;
  listType: string = LIST_TYPE.GRAPH_LIST;
  sortState;

  @Input() students$;
  @Input() studentSupports$;
  @Input() flags$;
  @Input() graphTitle;
  @Input() graphType;
  @Input() graphGroup;
  @Input() school: ISchool;
  @Input() milestones$;
  @Input() studentPaths$;

  constructor(
    private madLibGroupings: MadLibGroupings,
    private madLibHelpers: MadLibHelpers,
    private courseCodeGrouping: CourseCodeGrouping,
    @Inject('$state') private $state: any,
    @Inject('$stateParams') private $stateParams: any,
    private studentCollectionsMerger: StudentCollectionsMergeService,
  ) {
    // the selected graph that led us here
    this.graphName = this.$stateParams.graphName;
    this.midLevelGrouping = this.$stateParams.navDataType.toUpperCase();
    this.sortState = { sortedColumn: null, sortOrder: null, dataType: null };
  }

  ngOnInit() {
    // set student level header
    this.title = this.setTitle(this.$stateParams);

    // merge student with observables of other collections, this will update when any underlining $ changes
    this.studentsWithFlags$ = this.mergeStudentsAndCollections(
      this.students$,
      this.flags$,
      this.studentSupports$,
      this.milestones$,
      this.studentPaths$,
    );

    // since there is no mad-lib, columns are based on graph and grouping, here we get the columns
    this.formattedGraphName = VALID_FLAG_TYPES[this.graphName];

    const graph = this.graphName;

    const columnsForView = STUDENT_LEVEL_LIST_COLUMNS[graph][this.midLevelGrouping];

    this.columns = columnsForView;

    // TODO: improve arity of this function and others(JJ)
    this.studentGrouping$ = this.groupStudents(
      this.studentsWithFlags$,
      this.columns,
      this.graphType,
      this.graphGroup,
      this.formattedGraphName,
      this.listType,
    );
  }

  // TODO: improve arity of these functions(JJ) - unary++
  groupStudents(students$, columns: any[], graphType: string, flagCategory, flagType, listType): Observable<any> {
    switch (graphType) {
      case 'FLAG_BASED':
        return this.madLibGroupings.getFlagStatusGrouping(students$, columns, listType, flagCategory, flagType);
      case 'INFORMATIONAL':
        return this.madLibGroupings.getDefaultGrouping(students$, columns, listType, flagCategory, flagType);
      case 'SUPPORTED_SUBJECTS':
        if (this.midLevelGrouping === 'COURSE')
          return this.courseCodeGrouping.getGrouping(students$, {
            listType,
            columns,
            predicateValue: this.$stateParams.navData,
            mode: 'COURSE',
          });

        if (this.midLevelGrouping === 'SUBJECT')
          return this.courseCodeGrouping.getGrouping(students$, {
            listType,
            columns,
            predicateValue: this.$stateParams.navData,
            mode: 'SUBJECT',
          });

        if (this.midLevelGrouping === 'TEACHER')
          return this.courseCodeGrouping.getGrouping(students$, {
            listType,
            columns,
            predicateValue: this.$stateParams.navData,
            mode: 'TEACHER',
          });
      default:
        return this.madLibGroupings.getDefaultGrouping(students$, columns, listType, flagCategory, flagType);
    }
  }

  goToState(state: string) {
    this.$state.go(state, { graphName: this.graphName }, { location: 'replace' });
  }

  // TODO: move to service
  /* istanbul ignore next */
  mergeStudentsAndCollections(
    students$: Observable<IStudent[]>,
    flags$: Observable<IFlag[]>,
    studentSupports$: Observable<IStudentSupport[]>,
    milestones$: Observable<IMilestone[]>,
    studentPaths$: Observable<IStudentPath[]>,
  ) {
    return combineLatest(
      students$,
      flags$,
      studentSupports$,
      milestones$,
      studentPaths$,
      (
        students: IStudent[],
        flags: IFlag[],
        studentSupports: IStudentSupport[],
        milestones: IMilestone[],
        filteredStudentPaths: IStudentPath[],
      ) => {
        const studentsWithMergedCollections = this.studentCollectionsMerger.mergeCollectionsToStudents({
          students,
          collections: [
            {
              collection: flags,
              keyToAddToStudent: 'flags',
              pathToStudentId: `studentId`,
            } as ICollectionToMerge,
            {
              collection: studentSupports,
              keyToAddToStudent: 'activeStudentSupports',
              pathToStudentId: 'student.studentId',
            } as ICollectionToMerge,
            {
              collection: milestones,
              keyToAddToStudent: 'milestones',
              pathToStudentId: `studentId`,
            } as ICollectionToMerge,
            {
              collection: filteredStudentPaths,
              keyToAddToStudent: 'studentPaths',
              pathToStudentId: `studentId`,
            } as ICollectionToMerge,
          ],
        });

        return studentsWithMergedCollections;
      },
    );
  }

  setTitle(stateParams) {
    const { navData, navDataType } = stateParams;
    switch (navDataType) {
      case 'GRADE':
        return GRADE_MAPPING[navData].human;
      case 'CLASS_OF':
        return CLASS_OF_MAPPING[navData].human;
      case 'SUBJECT':
        return SubjectAreas[navData].human;
      default:
        return navData;
    }
  }

  sortData(sort) {
    const { key, dataType, orderBy } = sort;
    this.sortState = { sortedColumn: key, sortOrder: orderBy, dataType };
    const grouping$ = this.groupStudents(
      this.studentsWithFlags$,
      this.columns,
      this.graphType,
      this.graphGroup,
      this.formattedGraphName,
      this.listType,
    );

    const sortedGrouping$ = this.madLibHelpers.getSortedStudentGrouping(
      grouping$,
      key,
      dataType,
      orderBy,
      this.sortState,
      false,
    );

    this.studentGrouping$ = sortedGrouping$;
  }
}
